import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Facilities = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    facility_name: 'swimming_pool',
    booking_date: '',
    start_time: '',
    end_time: '',
    notes: ''
  });

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const { data } = await axios.get(`${API}/facilities/bookings`, { withCredentials: true });
      setBookings(data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/facilities/book`, formData, { withCredentials: true });
      toast.success('Booking submitted successfully');
      setShowForm(false);
      setFormData({ facility_name: 'swimming_pool', booking_date: '', start_time: '', end_time: '', notes: '' });
      loadBookings();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const handleApprove = async (bookingId) => {
    try {
      await axios.patch(`${API}/facilities/bookings/${bookingId}/approve`, {}, { withCredentials: true });
      toast.success('Booking approved');
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve booking');
    }
  };

  const [rejectId, setRejectId] = useState(null);
  const [rejectFacility, setRejectFacility] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const openReject = (bookingId, facilityLabel) => {
    setRejectId(bookingId);
    setRejectFacility(facilityLabel);
    setRejectReason('');
  };

  const closeReject = () => {
    setRejectId(null);
    setRejectFacility('');
    setRejectReason('');
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await axios.patch(
        `${API}/facilities/bookings/${rejectId}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true }
      );
      toast.success('Booking rejected');
      closeReject();
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject booking');
    }
  };

  const facilities = [
    { value: 'swimming_pool', label: 'Swimming Pool' },
    { value: 'gym', label: 'Gym' },
    { value: 'function_hall', label: 'Function Hall' },
    { value: 'bbq_area', label: 'BBQ Area' },
    { value: 'tennis_court', label: 'Tennis Court' },
    { value: 'meeting_room', label: 'Meeting Room' }
  ];

  return (
    <div className="space-y-6" data-testid="facilities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Facility Bookings</h1>
          <p className="text-sm text-[#64748b]">Book and manage community facilities</p>
        </div>
        {(user?.role === 'resident' || user?.role === 'owner') && (
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
            data-testid="new-booking-button"
          >
            <Calendar className="mr-2 h-4 w-4" />
            New Booking
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Facility</Label>
              <select
                value={formData.facility_name}
                onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm"
                data-testid="facility-select"
              >
                {facilities.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Date</Label>
                <Input
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                  required
                  data-testid="booking-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Start Time</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="start-time-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  data-testid="end-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any special requirements..."
                data-testid="booking-notes-input"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-booking-button">
                Submit Booking
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Bookings List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
          </div>
        ) : bookings.length === 0 ? (
          <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
            <Calendar className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
            <p className="text-[#64748b]">No bookings yet</p>
          </Card>
        ) : (
          bookings.map((booking, index) => (
            <Card key={index} className="p-6 bg-white border border-[#e2e8f0] hover:shadow-sm transition-shadow" data-testid={`booking-card-${index}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
                    {facilities.find(f => f.value === booking.facility_name)?.label || booking.facility_name}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-[#64748b] mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{booking.booking_date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{booking.start_time} - {booking.end_time}</span>
                    </div>
                  </div>
                  {booking.notes && <p className="text-sm text-[#64748b] mb-3">{booking.notes}</p>}
                  {booking.status === 'rejected' && booking.rejection_reason && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md" data-testid={`rejection-reason-${index}`}>
                      <p className="text-xs font-semibold uppercase tracking-widest text-red-700 mb-1">
                        Rejected{booking.rejected_by ? ` by ${booking.rejected_by}` : ''}
                      </p>
                      <p className="text-sm text-red-700">{booking.rejection_reason}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      booking.status === 'approved' ? 'bg-green-100 text-green-700' :
                      booking.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {booking.status}
                    </span>
                    {booking.user_name && <span className="px-3 py-1 bg-[#f1f5f9] text-xs font-medium rounded-full">By {booking.user_name}</span>}
                  </div>
                </div>
                {user?.role === 'management' && booking.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => handleApprove(booking._id)}
                      className="bg-[#10b981] hover:bg-[#059669] text-white"
                      data-testid={`approve-booking-${index}`}
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => openReject(booking._id, facilities.find(f => f.value === booking.facility_name)?.label || booking.facility_name)}
                      variant="outline"
                      className="border-[#ef4444] text-[#ef4444] hover:bg-red-50"
                      data-testid={`reject-booking-${index}`}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeReject}>
          <Card className="p-6 bg-white w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Reject Booking</h3>
                <p className="text-xs text-[#64748b]">{rejectFacility}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeReject} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleReject} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Reason *</Label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this booking cannot be approved..."
                  required
                  rows={4}
                  className="w-full px-3 py-2 rounded-md border border-[#e2e8f0] text-sm focus:outline-none focus:ring-2 focus:ring-[#ef4444]"
                  data-testid="reject-reason-input"
                  autoFocus
                />
                <p className="text-xs text-[#64748b]">The resident will see this reason on their booking.</p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={closeReject}>Cancel</Button>
                <Button type="submit" className="bg-[#ef4444] hover:bg-[#dc2626] text-white" data-testid="confirm-reject-button">
                  Reject Booking
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};
